// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a React hook for fetching and managing location reports.
 * It includes functionality for sorting reports by name or count, and filtering
 * out locations with no assets.
 */
import { Sort } from '@/constants/sort';
import {
  LocationFunctionalStatusReport,
  LocationOwnershipReport,
  getLocationReport,
} from '@/location/api/get-location-report';
import { useQuery } from '@tanstack/react-query';

/**
 * Valid properties for sorting location reports.
 * - 'name': Sort by location name
 * - 'count': Sort by total asset count
 */
type LocationReportSortProperty = 'name' | 'count';

/**
 * Type alias for sort configuration specific to location reports.
 */
export type LocationReportSort = Sort<LocationReportSortProperty>;

/**
 * Union type representing either a functional status report or ownership report.
 * Used internally for common sorting logic.
 */
type Report = LocationFunctionalStatusReport | LocationOwnershipReport;

/**
 * A React hook that fetches and processes location reports from the API.
 * Provides sorted and filtered data for both functional status and ownership reports.
 *
 * Features:
 * - Fetches both functional status and ownership reports in a single query
 * - Filters out locations with no assets (totalCount = 0)
 * - Sorts reports by either location name or total asset count
 * - Maintains separate arrays for functional status and ownership data
 *
 * @param sort - Configuration for sorting the reports (property and direction)
 * @returns Object containing:
 *   - functionalStatusData: Sorted array of functional status reports
 *   - ownershipData: Sorted array of ownership reports
 *   - Additional query metadata (loading state, error state, etc.)
 */
export function useLocationReport(sort: LocationReportSort) {
  const query = useQuery({
    queryKey: ['location-reports'],
    queryFn: getLocationReport,
  });

  /**
   * Compares two reports based on the current sort configuration.
   * Handles both name and count sorting in ascending or descending order.
   */
  const compare = (report: Report, other: Report) => {
    switch (sort.property) {
      case 'name':
        return sort.direction === 'asc'
          ? report.location.name.localeCompare(other.location.name)
          : other.location.name.localeCompare(report.location.name);

      case 'count':
        return sort.direction === 'asc'
          ? report.totalCount - other.totalCount
          : other.totalCount - report.totalCount;
    }
  };

  // Process functional status reports: filter out empty locations and sort
  const functionalStatusData =
    query.data?.functional
      .slice()
      .filter((report) => report.totalCount > 0)
      .sort(compare) ?? [];

  // Process ownership reports: filter out empty locations and sort
  const ownershipData =
    query.data?.ownership
      .slice()
      .filter((report) => report.totalCount > 0)
      .sort(compare) ?? [];

  return { ...query, functionalStatusData, ownershipData };
}
